import { Box, Card, Chip, Typography, Stack, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";

import FetchWorkstationsControl from "~/components/FetchWorkstationsControl";
import { selectHandRaisedForWorkstation } from "~/features/andon/andon.selectors";
import {
  selectFetchStatusEndpointsInterval,
  selectGridModeByIdMap,
  selectPortStatusesByPortKeyMap
} from "~/features/andon/andon.slice";
import {
  figureAndonColorFromStatus,
  getWorkstationPortFaultStatus
} from "~/features/andon/andon.utils";
import { extractUserNameFromAuth0Id } from "~/lib/helpers";
import { useGetAndonWorkstationsQuery } from "~/redux/warehouse/andon.hooks";
import { AndonWorkstation } from "~/types/api";

function MicroWorkstationStatus({
  workstation
}: {
  workstation: AndonWorkstation;
}) {
  const { t } = useTranslation();
  const portStatusesByPortKey = useAppSelector(selectPortStatusesByPortKeyMap);
  const gridSystemModeById = useAppSelector(selectGridModeByIdMap);
  const handRaised = useAppSelector(
    selectHandRaisedForWorkstation(workstation.workstationId)
  );

  const gridSystemMode =
    gridSystemModeById[workstation.autostoreGridId] ?? null;

  const { backgroundColor, borderColor, altTextKey } =
    figureAndonColorFromStatus({
      gridSystemMode,
      portStatus: getWorkstationPortFaultStatus(
        workstation.ports,
        workstation.autostoreGridId,
        portStatusesByPortKey
      ),
      workstationActive: workstation.status === "Active",
      handRaised: handRaised.raised,
      handRaiseRequestType: handRaised.requestType,
      workstationMode: workstation.mode
    });

  const userName = extractUserNameFromAuth0Id(workstation.userId || "") || "";
  const statusLabel = altTextKey ? t(altTextKey) : workstation.mode || "N/A";

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="body2">
            <strong>
              {workstation.deviceId || workstation.workstationId.slice(0, 8)}
            </strong>
          </Typography>
          {userName && (
            <Typography variant="caption" display="block">
              User: {userName}
            </Typography>
          )}
          <Typography variant="caption" display="block">
            Status: {statusLabel}
          </Typography>
          <Typography variant="caption" display="block">
            Mode: {workstation.mode || "N/A"}
          </Typography>
          {handRaised.raised && (
            <Typography variant="caption" color="warning.main" display="block">
              🖐️ {t("hand raised")}
            </Typography>
          )}
        </Box>
      }
    >
      <Box
        sx={{
          width: 12,
          height: 12,
          backgroundColor,
          border: `1px solid ${borderColor}`,
          borderRadius: 0.25,
          cursor: "pointer",
          transition: "transform 0.1s ease-in-out",
          "&:hover": {
            transform: "scale(1.5)"
          }
        }}
      />
    </Tooltip>
  );
}

export function MicroAndonBoard() {
  const { t } = useTranslation();
  const pollingInterval = useAppSelector(selectFetchStatusEndpointsInterval);
  const { data, isLoading } = useGetAndonWorkstationsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    skipPollingIfUnfocused: true,
    pollingInterval: pollingInterval > 0 ? pollingInterval : undefined
  });
  const workstations = data?.workstations;
  const gridSystemModeById = useAppSelector(selectGridModeByIdMap);

  if (isLoading) {
    return (
      <Card sx={{ p: 2, minWidth: 300, maxWidth: 400 }}>
        <Typography variant="body2" color="text.secondary">
          {t("loading")}...
        </Typography>
      </Card>
    );
  }

  if (!workstations || workstations.length === 0) {
    return null;
  }

  const workstationsByGrid: Record<
    string,
    { gridName: string; workstations: AndonWorkstation[] }
  > = {};
  for (const workstation of workstations) {
    const gridId = workstation.autostoreGridId;
    if (!workstationsByGrid[gridId]) {
      workstationsByGrid[gridId] = {
        gridName: workstation.autostoreGridName,
        workstations: []
      };
    }
    workstationsByGrid[gridId].workstations.push(workstation);
  }

  return (
    <Card
      sx={{
        p: 1.5,
        minWidth: 200,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        boxShadow: 1
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        mb={1}
        flexWrap="wrap"
      >
        <Typography variant="subtitle2" component="div">
          {t("andon board")}
        </Typography>
        <FetchWorkstationsControl />
      </Stack>

      <Stack spacing={1}>
        {Object.entries(workstationsByGrid).map(
          ([gridId, { gridName, workstations: gridWorkstations }]) => {
            const gridMode = gridSystemModeById[gridId];
            const isGridError =
              gridMode && ["ALT", "DSTP", "SVC", "STP"].includes(gridMode);

            return (
              <Box key={gridId}>
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.65rem" }}
                  >
                    {gridName}
                  </Typography>
                  {isGridError && (
                    <Chip
                      label={gridMode}
                      size="small"
                      color="error"
                      sx={{ height: 14, fontSize: "0.55rem" }}
                    />
                  )}
                </Box>
                <Box display="flex" flexWrap="wrap" gap={0.25}>
                  {gridWorkstations.map((workstation) => (
                    <MicroWorkstationStatus
                      key={workstation.workstationId}
                      workstation={workstation}
                    />
                  ))}
                </Box>
              </Box>
            );
          }
        )}
      </Stack>

      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ fontSize: "0.6rem", mt: 1, display: "block" }}
      >
        Hover for details
      </Typography>
    </Card>
  );
}

export default MicroAndonBoard;
